import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Menu, ChevronRight } from 'lucide-react';

const SIDEBAR_COOKIE_NAME = "sidebar:state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days
const SIDEBAR_WIDTH = "clamp(16rem, 32vw, 24rem)";
const SIDEBAR_WIDTH_ICON = "4.2rem"; // Increased from 3rem to fit icons + labels
const SIDEBAR_HEIGHT_MOBILE = "25vh"; // Mobile expanded height (25% of viewport)
const SIDEBAR_HEIGHT_MOBILE_ICON = "3.5rem"; // Mobile collapsed height (horizontal icon bar)
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

// Context
const SidebarContext = createContext(null);

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}

// Provider
export function SidebarProvider({
  defaultOpen = false,
  open: openProp,
  onOpenChange: setOpenProp,
  children,
  ...props
}) {
  const [_open, _setOpen] = useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = useCallback(
    (value) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open]
  );

  const toggleSidebar = useCallback(() => {
    setOpen((prev) => !prev);
  }, [setOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  const state = open ? "expanded" : "collapsed";

  const contextValue = {
    state,
    open,
    setOpen,
    toggleSidebar,
  };

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        style={{
          "--sidebar-width": SIDEBAR_WIDTH,
          "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
          "--sidebar-height-mobile": SIDEBAR_HEIGHT_MOBILE,
          "--sidebar-height-mobile-icon": SIDEBAR_HEIGHT_MOBILE_ICON,
        }}
        className="flex min-h-screen w-full md:flex-row flex-col"
        data-sidebar-state={state}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
}

// Sidebar
export const Sidebar = React.forwardRef(
  ({ side = "left", variant = "sidebar", collapsible = "icon", className, children, ...props }, ref) => {
    const { state } = useSidebar();

    return (
      <aside
        ref={ref}
        data-state={state}
        data-collapsible={collapsible}
        data-side={side}
        data-variant={variant}
        className={[
          "group/sidebar",
          "flex flex-col bg-white border-gray-200",
          "transition-all duration-200 ease-linear",
          "fixed z-50",
          // Desktop: left side, full height, adjust width
          "md:left-0 md:top-0 md:h-screen md:border-r",
          state === "expanded" ? "md:w-[var(--sidebar-width)]" : "md:w-[var(--sidebar-width-icon)]",
          // Mobile: bottom, full width, adjust height  
          "bottom-0 left-0 right-0",
          "border-t md:border-t-0",
          state === "expanded" ? "h-[var(--sidebar-height-mobile)]" : "h-[var(--sidebar-height-mobile-icon)]",
          "md:h-screen",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        {...props}
      >
        {children}
      </aside>
    );
  }
);
Sidebar.displayName = "Sidebar";

// SidebarTrigger
export const SidebarTrigger = React.forwardRef(({ className, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      ref={ref}
      onClick={toggleSidebar}
      className={[
        "inline-flex items-center justify-center",
        "h-7 w-7 rounded-md",
        "hover:bg-gray-100 active:bg-gray-200",
        "transition-colors",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      {...props}
    >
      <Menu className="h-4 w-4" />
      <span className="sr-only">Toggle Sidebar</span>
    </button>
  );
});
SidebarTrigger.displayName = "SidebarTrigger";

// SidebarHeader
export const SidebarHeader = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={["flex flex-col gap-2 p-2 border-b border-gray-200", className]
        .filter(Boolean)
        .join(" ")}
      {...props}
    />
  );
});
SidebarHeader.displayName = "SidebarHeader";

// SidebarFooter
export const SidebarFooter = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={["flex flex-col gap-2 p-2 border-t border-gray-200 mt-auto", className]
        .filter(Boolean)
        .join(" ")}
      {...props}
    />
  );
});
SidebarFooter.displayName = "SidebarFooter";

// SidebarContent
export const SidebarContent = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={["flex-1 overflow-y-auto overflow-x-hidden", className]
        .filter(Boolean)
        .join(" ")}
      {...props}
    />
  );
});
SidebarContent.displayName = "SidebarContent";

// SidebarGroup
export const SidebarGroup = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={["flex flex-col gap-2 p-2", className].filter(Boolean).join(" ")}
      {...props}
    />
  );
});
SidebarGroup.displayName = "SidebarGroup";

// SidebarGroupLabel
export const SidebarGroupLabel = React.forwardRef(({ className, ...props }, ref) => {
  const { state } = useSidebar();

  return (
    <div
      ref={ref}
      className={[
        "px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider",
        state === "collapsed" && "opacity-0",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      {...props}
    />
  );
});
SidebarGroupLabel.displayName = "SidebarGroupLabel";

// SidebarGroupContent
export const SidebarGroupContent = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={["flex flex-col gap-1", className].filter(Boolean).join(" ")}
      {...props}
    />
  );
});
SidebarGroupContent.displayName = "SidebarGroupContent";

// SidebarMenu
export const SidebarMenu = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <ul
      ref={ref}
      className={["flex flex-col gap-1", className].filter(Boolean).join(" ")}
      {...props}
    />
  );
});
SidebarMenu.displayName = "SidebarMenu";

// SidebarMenuItem
export const SidebarMenuItem = React.forwardRef(({ className, ...props }, ref) => {
  return <li ref={ref} className={["list-none", className].filter(Boolean).join(" ")} {...props} />;
});
SidebarMenuItem.displayName = "SidebarMenuItem";

// SidebarMenuButton
export const SidebarMenuButton = React.forwardRef(
  ({ asChild = false, isActive = false, className, children, ...props }, ref) => {
    const { state } = useSidebar();
    const Comp = asChild ? React.Fragment : "button";

    const buttonClasses = [
      "flex items-center gap-2 w-full",
      "px-2 py-1.5 rounded-md",
      "text-sm text-gray-700",
      "hover:bg-gray-100 active:bg-gray-200",
      "transition-colors",
      isActive && "bg-gray-100 font-medium",
      state === "collapsed" && "justify-center",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    if (asChild) {
      return React.cloneElement(React.Children.only(children), {
        ref,
        className: buttonClasses,
        ...props,
      });
    }

    return (
      <Comp ref={ref} className={buttonClasses} {...props}>
        {children}
      </Comp>
    );
  }
);
SidebarMenuButton.displayName = "SidebarMenuButton";

// SidebarSeparator
export const SidebarSeparator = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={["h-px bg-gray-200 my-2", className].filter(Boolean).join(" ")}
      {...props}
    />
  );
});
SidebarSeparator.displayName = "SidebarSeparator";
